import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowUpRight, CreditCard, LineChart, Loader2, Users } from "lucide-react";

import { AdminShell } from "@/platform/layout/admin-shell";
import { fetchAdminOverview } from "@/platform/admin/api";

export function AdminIndexPage() {
  const query = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: fetchAdminOverview,
    staleTime: 60_000,
  });

  return (
    <AdminShell
      title="Visão geral"
      description="Estado da operação cross-tenant: usuárias, receita recorrente, atividade."
    >
      {query.isLoading ? (
        <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
      ) : query.error ? (
        <p className="text-[length:var(--text-body-sm)] text-[color:var(--color-danger)]">
          Falha ao carregar visão geral.
        </p>
      ) : query.data ? (
        <>
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label="Usuárias"
              value={query.data.users.total.toLocaleString("pt-BR")}
              hint={`+${query.data.users.last7d} nos últimos 7 dias`}
            />
            <Kpi
              label="Assinaturas ativas"
              value={query.data.subscriptions.active.toLocaleString("pt-BR")}
              hint={`${query.data.subscriptions.trialing} em trial · ${query.data.subscriptions.pastDue} past_due`}
            />
            <Kpi
              label="MRR estimado"
              value={`R$ ${query.data.mrr.totalBrl.toLocaleString("pt-BR")}`}
              hint={`Einstein R$ ${query.data.mrr.einsteinBrl.toLocaleString("pt-BR")} · USP R$ ${query.data.mrr.uspBrl.toLocaleString("pt-BR")}`}
            />
            <Kpi
              label="LLM (24h)"
              value={query.data.llm.last24h.toLocaleString("pt-BR")}
              hint={`${query.data.llm.errors24h} erros · ${query.data.llm.avgLatencyMs}ms médio`}
            />
          </section>

          <section className="mt-10 grid gap-5 lg:grid-cols-3">
            <ShortcutCard
              to="/admin/usuarios"
              icon={Users}
              title="Gerenciar usuárias"
              description="Listar, filtrar, banir, conceder admin, ver progresso individual."
            />
            <ShortcutCard
              to="/admin/assinaturas"
              icon={CreditCard}
              title="Assinaturas"
              description="Ver status, cancelar, pular cobrança, conferir histórico de pagamento."
            />
            <ShortcutCard
              to="/admin/metricas"
              icon={LineChart}
              title="Métricas de negócio"
              description="MRR, conversão trial→pago, churn, breakdown por plano e tenant."
            />
          </section>
        </>
      ) : null}
    </AdminShell>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)]">
      <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {label}
      </p>
      <p
        data-tabular="true"
        className="mt-2 font-display text-[length:var(--text-display-sm)] font-semibold leading-none tabular-nums"
      >
        {value}
      </p>
      {hint ? (
        <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function ShortcutCard({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: typeof Users;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group relative flex flex-col rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] lift"
    >
      <span
        className="chip size-10"
        style={{
          backgroundColor: "var(--color-accent-soft)",
          color: "var(--color-accent-soft-foreground)",
        }}
      >
        <Icon className="size-5" aria-hidden />
      </span>
      <h3 className="mt-5 font-display text-[length:var(--text-body-lg)] font-semibold leading-tight">
        {title}
      </h3>
      <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed flex-1">
        {description}
      </p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)] transition-colors group-hover:text-[color:var(--color-foreground)]">
        Abrir
        <ArrowUpRight className="size-3" aria-hidden />
      </span>
    </Link>
  );
}
